/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════
 * T-31 PIEZA 3 — EL PANEL DE DUPLICADO. **MUESTRA, NO PREGUNTA.**
 * ═══════════════════════════════════════════════════════════════════════════════════════════
 *
 * 🔴 QUÉ REEMPLAZA Y POR QUÉ. La advertencia vieja era un `window.confirm`:
 *
 *     "Ya existe un recibo similar con la misma fecha y monto: … ¿Está seguro que este no es
 *      un duplicado?"
 *
 * **Se acepta sin leer.** Y peor: en los casos de días distintos —que son la mayoría de los
 * incidentes reales medidos en prod— la persona **no recuerda la primera carga**, así que
 * preguntarle "¿está seguro?" no le da con qué decidir. Preguntar sin mostrar delega en la
 * memoria de alguien un dato que el sistema ya tiene.
 *
 * ⚠⚠ POR ESO LA REQUISICIÓN ESTÁ EN LA LISTA. La diferencia entre *"subí esto dos veces"* y
 * *"esto ya se pagó"* es lo único que realmente importa, y no se puede adivinar mirando una
 * fecha. Una requisición **es un cheque de Sage**: si la previa la tiene, esto es plata que ya
 * salió.
 *
 * ⚠ PEPÍN VA A ADVERTIR SIEMPRE. Panadería Pepín reutiliza los números de factura, así que sus
 * recibos matchean el nivel 3 una y otra vez. Eso es tolerable **solo** porque este panel se
 * resuelve de un vistazo —"ah, esa es la de abril"— en vez de ser una pregunta a ciegas. Si el
 * panel volviera a ser un `confirm`, Pepín lo convertiría en ruido que se acepta por reflejo.
 *
 * 🔴 EL CONTROL ES DEL SERVIDOR, NO DE ACÁ. Este panel es la conversación. El 409 llega antes
 * de que se escriba nada, y un cliente que no lo implemente no puede seguir de todos modos —
 * lo contrario sería la autoridad aparente que V82 dejó documentada.
 */
import { useState } from 'react';
import { NAVY, BORDER_COLOR } from '../../styles/tokens';

const ESTADO_ES = {
  draft: 'borrador', pending: 'pendiente', pending_review: 'en revisión',
  approved: 'aprobado', paid: 'pagado', rejected: 'rechazado',
  sent_back: 'devuelto', partially_returned: 'devuelto parcial', voided: 'anulado',
};

const SEÑAL_ES = {
  mismoArchivo: 'mismo archivo', mismaFactura: 'misma factura',
  mismoProveedor: 'mismo proveedor', mismaFecha: 'misma fecha', mismoTotal: 'mismo monto',
};

export default function DuplicadoModal({ datos, onCancelar, onContinuar }) {
  const [motivo, setMotivo] = useState('');
  if (!datos) return null;

  const { nivel, error, requiere, coincidencias = [] } = datos;
  const sinSalida = nivel === 'bloqueo';
  const necesitaMotivo = requiere === 'motivo_duplicado';
  const puedeSeguir = !sinSalida && (!necesitaMotivo || motivo.trim().length >= 10);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 2147483600,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, border: `1.5px solid ${BORDER_COLOR}`,
        maxWidth: 620, width: '100%', maxHeight: '86vh', overflowY: 'auto',
        fontFamily: 'Calibri, Inter, sans-serif',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${BORDER_COLOR}` }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: sinSalida ? '#b91c1c' : NAVY }}>
            {sinSalida ? 'Este recibo ya está cargado' : 'Puede que este recibo ya esté cargado'}
          </div>
          <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>{error}</div>
        </div>

        <div style={{ padding: '14px 20px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8 }}>
            {coincidencias.length === 1 ? 'RECIBO ANTERIOR' : `${coincidencias.length} RECIBOS ANTERIORES`}
          </div>

          {coincidencias.map(c => (
            <div key={c.id} style={{
              border: `1px solid ${c.exportado ? '#fca5a5' : BORDER_COLOR}`,
              background: c.exportado ? '#fef2f2' : '#f8fafc',
              borderRadius: 10, padding: '10px 12px', marginBottom: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, color: NAVY, fontSize: 14 }}>
                  {c.receiptNumber || `#${c.id}`} · {c.vendor || '—'}
                </span>
                <span style={{ fontWeight: 700, fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
                  ${Number(c.receiptTotal ?? 0).toFixed(2)}
                </span>
              </div>
              <div style={{ fontSize: 12.5, color: '#475569', marginTop: 3 }}>
                {c.date || '—'} · {ESTADO_ES[c.status] || c.status} · subido por {c.uploader || '—'}
                {c.invoiceNumber ? ` · factura ${c.invoiceNumber}` : ''}
              </div>

              {/* 🔴 La línea que decide. Sin ella el panel informa; con ella, alerta. */}
              {(c.exportado || c.requisiciones) && (
                <div style={{
                  marginTop: 6, fontSize: 12.5, fontWeight: 700, color: '#b91c1c',
                  background: '#fee2e2', border: '1px solid #fca5a5',
                  borderRadius: 6, padding: '4px 8px', display: 'inline-block',
                }}>
                  {c.requisiciones
                    ? `⚠ YA SE PAGÓ — requisición ${c.requisiciones}`
                    : '⚠ Ya se exportó a Sage'}
                </div>
              )}

              <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 5 }}>
                Coincide en: {Object.entries(c.señales || {})
                  .filter(([, v]) => v).map(([k]) => SEÑAL_ES[k] || k).join(', ') || '—'}
              </div>
            </div>
          ))}

          {necesitaMotivo && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>
                MOTIVO (requerido, queda registrado)
              </div>
              <textarea
                value={motivo} onChange={e => setMotivo(e.target.value)} rows={2}
                placeholder="Ej: son dos compras distintas del mismo día, verificado con el proveedor"
                style={{
                  width: '100%', padding: 8, borderRadius: 8, fontSize: 13,
                  border: `1px solid ${BORDER_COLOR}`, fontFamily: 'inherit', resize: 'vertical',
                }} />
              {/* ⚠ El mínimo existe para que el motivo sea una explicación y no un "ok". */}
              {motivo.trim().length > 0 && motivo.trim().length < 10 && (
                <div style={{ fontSize: 11.5, color: '#b45309', marginTop: 3 }}>
                  Explicá brevemente por qué no es un duplicado.
                </div>
              )}
            </div>
          )}

          {sinSalida && (
            <div style={{ fontSize: 12.5, color: '#475569', marginTop: 10 }}>
              Es exactamente el mismo archivo. Si de verdad es un gasto distinto, sacale una foto
              nueva al comprobante.
            </div>
          )}
        </div>

        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${BORDER_COLOR}`,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button type="button" onClick={onCancelar} style={{
            padding: '8px 14px', borderRadius: 8, border: `1px solid ${BORDER_COLOR}`,
            background: '#fff', color: NAVY, fontWeight: 600, fontSize: 13, cursor: 'pointer',
            fontFamily: 'inherit',
          }}>{sinSalida ? 'Entendido' : 'Cancelar'}</button>

          {!sinSalida && (
            <button type="button" disabled={!puedeSeguir}
              onClick={() => onContinuar(necesitaMotivo
                ? { motivo_duplicado: motivo.trim() }
                : { confirmar_duplicado: true })}
              style={{
                padding: '8px 14px', borderRadius: 8, border: 'none',
                background: puedeSeguir ? NAVY : '#cbd5e1', color: '#fff',
                fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                cursor: puedeSeguir ? 'pointer' : 'not-allowed',
              }}>
              No es duplicado, continuar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
